import useTranslation from '../useTranslation';
import type { FeatureCollection, GeoJsonProperties, Point } from 'geojson';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';
import type { DetailsViewEntityMenuItem } from 'components/DetailsViewEntityMenu/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundary from 'hooks/useBoundary';
import useClient from 'hooks/useClient';
import type { ClientResponse } from 'hooks/useClient/types';
import useFarms from 'hooks/useFarms';
import type { ParcelResponse } from 'hooks/useFarms/types';
import usePageRoutes from 'hooks/usePageRoutes';
import type { PaginatedGenerator } from 'hooks/usePagination/types';

import {
    DETAIL_TYPE_CONTACT_NAME,
    DETAIL_TYPE_EMAIL,
    DETAIL_TYPE_PHONE
} from 'pages/ClientDetailsPage/constants';

const useData = () => {
    const { clientId } = useParams();
    const { EDIT_CLIENT } = useTranslation();
    const { fetchClient, isLoading: isClientLoading } = useClient();
    const { clientsPage, farmDetailsPage } = usePageRoutes();
    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentClient, setCurrentClient] = useState<ClientResponse | undefined>(undefined);
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);
    const {
        farms,
        fetch: fetchFarms,
        isLoading: isFarmsLoading,
        totalCount: totalFarmsCount
    } = useFarms();
    const farmsGenerator = useRef<PaginatedGenerator<ParcelResponse> | null>(null);

    const farmMenuItems: DetailsViewEntityMenuItem[] = (farms ?? []).map(farm => ({
        entityId: farm?.id ?? '',
        entityName: farm?.name ?? '',
        entityRoute: farmDetailsPage(`${farm?.id}`)
    }));
    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentClient?.contactName ?? '',
            entityType: DETAIL_TYPE_CONTACT_NAME
        },
        {
            entityName: currentClient?.email ?? '',
            entityType: DETAIL_TYPE_EMAIL
        },
        {
            entityName: currentClient?.phone ?? '',
            entityType: DETAIL_TYPE_PHONE
        }
    ];

    const getCurrentClient = async () => {
        if (clientId) {
            const client = await fetchClient({ id: clientId });

            farmsGenerator.current = fetchFarms({ clientId });
            await farmsGenerator.current.next();

            setCurrentClient(client ?? undefined);
            fetchApplications({ clientId }).next();
        }
    };

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (clientId) {
            getCurrentClient();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleFarmNext = async () => {
        if (!farmsGenerator.current) {
            return { done: true };
        }

        return farmsGenerator.current.next();
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);
            getCurrentClient();
        }
    };

    const dropdownMenuItems = [
        {
            onClick: handleEditClick,
            text: EDIT_CLIENT
        }
    ];

    const handleNavigateBack = () => {
        navigate(clientsPage);
    };

    const locations: [number, number][] = [
        [-122.6765, 45.5231],
        [-122.6824, 45.5051],
        [-122.6604, 45.548],
        [-122.689, 45.5289],
        [-122.6367, 45.559],
        [-122.6985, 45.4872],
        [-122.6417, 45.5152],
        [-122.7635, 45.6029],
        [-122.5945, 45.5146],
        [-122.6819, 45.4947],
        [-121.3153, 44.0582],
        [-121.6883, 43.9794]
    ];

    const geoJsonMock: FeatureCollection<Point, GeoJsonProperties> = {
        features: locations.map(([lng, lat]) => ({
            geometry: { coordinates: [lng, lat], type: 'Point' },
            properties: {},
            type: 'Feature'
        })),
        type: 'FeatureCollection'
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    return {
        applications,
        currentClient,
        dropdownMenuItems,
        entitiesLink,
        farmMenuItems,
        geoJsonMock,
        handleClose,
        handleEditClick,
        handleEditClose,
        handleFarmNext,
        handleNavigateBack,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClientLoading,
        isClosing,
        isEditing,
        isFarmsLoading,
        isLinkedEntitiesOpen,
        locations,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing,
        totalFarmsCount
    };
};

export default useData;
